import React from "react";
import { Box, Divider, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { getCommentAndColor } from "components/PerformanceReview/helpers";
import {
  Categories,
  CategoriesDescription,
  CategoriesMetrics,
  CategoryMap,
  SubCategories,
  SubCategoriesDescription,
  SubCategoryMap,
} from "components/PerformanceReview/constants";

import ParameterTag from "./ParameterTag";
import { buildTips, parseScore } from "./helpers";

/**
 * One performance section (Speech or Content) — mobile port of desktop
 * `ParameterwiseFeedback`.
 *
 * Header shows "{parameter} performance in this question: {overall}/100", then a
 * tappable list of parameter rows (name + score + band chip). The selected row
 * expands its metric description, sub-feature detail, and "How to improve" tips.
 *
 * @param {string} parameter   "Speech" | "Content"
 * @param {Array}  featureData [{ category, score, tags }]
 * @param {number} overall     0–100
 */
export default function ParameterSection({ parameter, featureData = [], overall = 0 }) {
  const [selected, setSelected] = React.useState(0);
  const active = featureData[selected] || featureData[0] || {};

  const overallColor = getCommentAndColor(overall).color;

  return (
    <Box
      sx={{
        bgcolor: "shades.clr-white-900",
        border: "1px solid",
        borderColor: "neutral.clr-100",
        borderRadius: 3,
        p: 4,
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <Typography variant="h6-semiBold" color="neutral.clr-800">
        {parameter} performance in this question:{" "}
        <Box component="span" sx={{ color: overallColor }}>
          {overall}/100
        </Box>
      </Typography>

      {/* Parameter rows */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {featureData.map((d, index) => {
          const band = getCommentAndColor(d.score);
          const isActive = index === selected;
          const scoreColor = band.fontColor || band.color;

          return (
            <Box
              key={d.category}
              role="button"
              onClick={() => setSelected(index)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                px: 3,
                py: 2.5,
                borderRadius: 2,
                cursor: "pointer",
                border: "1px solid",
                borderColor: isActive ? "primary.clr-300" : "neutral.clr-200",
                boxShadow: isActive ? "0px 2px 8px 0px #1961BF33" : "none",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body01-semiBold" color="neutral.clr-800">
                  {Categories[d.category]}
                </Typography>
                <Tooltip text={CategoriesDescription[d.category]} />
                <Typography variant="body01-bold" sx={{ color: scoreColor, ml: 1 }}>
                  {parseScore(d.score)}
                </Typography>
              </Box>
              <Box
                sx={{
                  px: 2,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: band.bgColor,
                }}
              >
                <Typography variant="body02-semiBold" sx={{ color: scoreColor }}>
                  {band.tag}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Selected parameter detail */}
      <Box>
        <Typography variant="body01-semiBold" color="neutral.clr-900">
          {CategoriesMetrics[active.category]}
        </Typography>

        <Divider sx={{ my: 3, borderColor: "neutral.clr-200" }} />

        <SubFeatures category={active.category} tags={active.tags} />

        <Tips featureData={active} />
      </Box>
    </Box>
  );
}

function Tooltip({ text }) {
  return (
    <InfoOutlinedIcon
      titleAccess={text}
      sx={{ fontSize: 14, color: "neutral.clr-400" }}
    />
  );
}

/**
 * Sub-feature detail. FLUENCY exposes tappable sub-category chips (Fillers /
 * Pauses / Disfluency); other categories list their tags directly. Mirrors
 * desktop `SubFeatures`.
 */
function SubFeatures({ category, tags }) {
  const [sub, setSub] = React.useState(0);

  const fluencySubs = React.useMemo(() => {
    if (category !== CategoryMap.FLUENCY) return [];
    return (tags || []).map((t) => t.subCategory);
  }, [category, tags]);

  React.useEffect(() => {
    setSub(0);
  }, [category]);

  if (!tags) return null;

  if (category === CategoryMap.FLUENCY) {
    if (!fluencySubs.length) {
      return (
        <Typography variant="body01-semiBold" color="primary.clr-300">
          Well done! Your speech was quite fluent in this response.
        </Typography>
      );
    }

    const activeTag = tags[sub];

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="body01-bold" color="neutral.clr-600">
          Click to see why your score was affected
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 2 }}>
          {fluencySubs.map((subCat, i) => {
            const isActive = i === sub;
            return (
              <Box
                key={subCat}
                role="button"
                onClick={() => setSub(i)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                  border: "1px solid",
                  cursor: "pointer",
                  borderColor: isActive ? "primary.clr-300" : "neutral.clr-200",
                }}
              >
                <Typography
                  variant="body02-bold"
                  color={isActive ? "primary.clr-300" : "neutral.clr-900"}
                >
                  {SubCategories[subCat]}
                </Typography>
                <Tooltip text={SubCategoriesDescription[subCat]} />
              </Box>
            );
          })}
        </Box>
        <Box sx={{ mt: 2 }}>
          <ParameterTag tag={activeTag} />
        </Box>
      </Box>
    );
  }

  // Non-fluency: list each tag directly (DELIVERY hides its labels).
  return (
    <Box sx={{ mb: 3, display: "flex", flexDirection: "column", gap: 2 }}>
      {(tags || []).map((tag, i) => (
        <Box key={i}>
          {category !== CategoryMap.DELIVERY &&
            tag.subCategory !== SubCategoryMap.JARGON_WORDS && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Typography variant="body01-bold" color="neutral.clr-700">
                  {SubCategories[tag.subCategory]}
                </Typography>
                <Tooltip text={SubCategoriesDescription[tag.subCategory]} />
              </Box>
            )}
          <ParameterTag tag={tag} />
        </Box>
      ))}
    </Box>
  );
}

function Tips({ featureData }) {
  const { tips, successMessage } = React.useMemo(
    () => buildTips(featureData),
    [featureData]
  );

  if (!tips.length) {
    return successMessage ? (
      <Typography variant="body01-semiBold" color="primary.clr-300">
        {successMessage}
      </Typography>
    ) : null;
  }

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "secondary.clr-300",
        bgcolor: "secondary.clr-100",
        borderRadius: 2,
        p: 3,
      }}
    >
      <Typography variant="body01-semiBold" color="neutral.clr-800">
        How to improve:-
      </Typography>
      <Box component="ul" sx={{ m: 0, mt: 1, pl: 5 }}>
        {/* Desktop adds a "read the blog" bullet here linking to its Resources
            list + /blog/:id reader. Mobile has neither screen, and we never send
            a user to a route without a mobile version — so the bullet is omitted
            rather than left as a link with nowhere to go. */}
        {tips.map((tip, i) => (
          <li key={i}>
            <Typography variant="body01-regular" color="neutral.clr-700">
              {tip}
            </Typography>
          </li>
        ))}
      </Box>
    </Box>
  );
}
